// @flow
import { getIn } from 'formik';
import {
  areAllValuesInGivenArrayNonNullOrNonEmptyArray,
  getValuesOfGivenKeysFromObjectAsArray,
} from '@src/common-utils/utils';
import { CUSTOM_DISCOUNTS_SPECIAL_CONFIG } from '@components/custom-discounts/config';

import {
  ALL_IN_SMALL_CASE,
  CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME,
  CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  MLD_IS_ADMIN_EDITED_BACKEND_NAME,
  GD_RANGES_BACKEND_NAME,
  IS_ROW_FROZEN,
  IS_ROW_OPENED_FOR_EDITING,
  IS_VBD,
  METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_BACKEND_NAMES,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_LABELS_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_VALUES_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_ID_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_LABEL_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_MLD_ID_BACKEND_NAME,
  IS_ADMIN_EDITED_NAME,
  ROW_ID,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_USAGE_BACKEND_NAME,
  RATE_CARD_ID_BACKEND_NAME,
} from '../../constants';
import { DIMENSIONS_FOR_WHICH_MULTI_SELECT_IS_ENABLED } from '../common/vbd/constants';
import { IS_IMPORTED_BACKEND_NAME } from '../renewal-automation/constants';
import { TEXT_NUMBER_INPUT_TYPE } from '../../formik-utils/consts';
import { toastError } from '../presentational/notifications/utils';

import {
  getUsageLabelWithUnitForMetric,
  getUsageTooltipForMetric,
  shouldShowUsageContainerForGivenMetric,
} from './mld-for-field/utils';

export const extractDimValuesFromRow = (row, keys) => {
  const values = [];
  for (const key of keys) {
    values.push(row[key]);
  }
  return values;
};

export const extractDimRelatedKeysFromGivenRowValues = (inProgressRow) => {
  const allKeys = Object.keys(inProgressRow);
  const nonDimRelatedKeys = [
    IS_ROW_FROZEN,
    IS_ROW_OPENED_FOR_EDITING,
    METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME,
    ROW_ID,
    METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME,
    METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_MLD_ID_BACKEND_NAME,
    IS_VBD,
    METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_VALUES_BACKEND_NAME,
    RATE_CARD_ID_BACKEND_NAME,
    METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_USAGE_BACKEND_NAME,
  ];

  return allKeys.filter((x) => !nonDimRelatedKeys.includes(x));
};

export const getMetricNameOptions = (metricsList, rowValues, allRows) => {
  const chosenMetricName =
    rowValues[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME];

  // Case 1: There is already a Metric Name which is selected. In this case, just return that single value for the UI to display it
  if (chosenMetricName) {
    const chosenMetricRow = metricsList.filter((x) => x.name === chosenMetricName)[0];
    // If the Metric is applicable to the chosen RC, then, just send back an array with that just one value
    if (chosenMetricRow) {
      return [
        {
          name: chosenMetricRow.name,
          display_name: chosenMetricRow.label,
        },
      ];
    } else {
      // Sometimes, when the UI is getting refreshed, the chosenMetricRow might be null because the UI is in transit
      // e.g.,select CKUs in 4/2; and Cluster Base in 3/5; then move from 4/2 -> 3/5 -> 4/2
      // while the UI is in transit mode, we can see that chosenMetricRow is null
      // Hence, send an empty array for the time being. The next render will take care of setting the right values
      return [];
    }
  }

  // Case 2: When Metric Name is not selected, get the list of all unused metrics and display those
  const usedMetrics = allRows.map(
    (x) => x[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME]
  );
  const unusedMetricsList = metricsList.filter((x) => !usedMetrics.includes(x.name));
  return unusedMetricsList.map((x) => ({
    name: x.name,
    display_name: x.label,
    shouldNotBeSelectable: x.shouldNotBeSelectable,
  }));
};

export const metricNameDeleteOnClickHandler = async (
  rowValues,
  deleteFunc,
  index,
  arrayHelpers
) => {
  const dbRowIds = rowValues[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME].map(
    (x) => x.id
  );

  if (dbRowIds.length > 0) {
    const payload = {
      db_row_ids: dbRowIds,
    };

    const { error } = await deleteFunc({
      payload,
    });

    if (error) {
      toastError(
        error,
        'There was an error in deleting the metric, please contact #cloud-commitment-estimator channel!'
      );
    } else {
      arrayHelpers.remove(index);
    }
  } else {
    arrayHelpers.remove(index);
  }
};

const getColDefaultValue = (checkForMultiSelectability, dependentDimension) => {
  if (checkForMultiSelectability) {
    return DIMENSIONS_FOR_WHICH_MULTI_SELECT_IS_ENABLED.includes(dependentDimension.name)
      ? []
      : null;
  } else {
    return null;
  }
};

export const getSpecialMLDConfigForGivenMetricAndDimension = (metricName, dimName) =>
  CUSTOM_DISCOUNTS_SPECIAL_CONFIG?.[metricName]?.[dimName];

const addAnySpecialPropsIfExistsAndReturnNewConfig = (metricName, dimName, existingColConfig) => {
  let configToRet = { ...existingColConfig };
  const specialConfig = getSpecialMLDConfigForGivenMetricAndDimension(metricName, dimName);
  if (specialConfig?.specialDependentDimensionProps) {
    configToRet = {
      ...existingColConfig,
      ...specialConfig.specialDependentDimensionProps,
    };
  }

  return configToRet;
};

export const getDependentDimensionsColumnConfig = (
  dependentDimensions,
  metricName,
  dynamicWidthAllocation = false,
  checkForMultiSelectability = false,
  hasUsageColumnConcept = true
) => {
  const columnConfig = [];
  const dimensionsSeenSoFar = [];
  const allDimensionNames = dependentDimensions.map((x) => x.name);
  let columnWidthForEachDependentDimension = 3;
  const shouldShowUsageContainer =
    hasUsageColumnConcept && shouldShowUsageContainerForGivenMetric(metricName);
  if (dynamicWidthAllocation) {
    const totalNumberOfDependentDimensions = dependentDimensions.length;
    if (totalNumberOfDependentDimensions > 0) {
      /*
    Total available columns = 16
    Usage, Discount, Update, Delete will occupy 2+2+1+1 = 6
    Remaining Space = 10 if Usage is shown, else 12
    Divide this equally. Also ensure that none of the cols go beyond 3
   */
      const availableSpaceForDepDims = shouldShowUsageContainer ? 10 : 12;
      columnWidthForEachDependentDimension = Math.min(
        Math.floor(availableSpaceForDepDims / totalNumberOfDependentDimensions),
        3
      );
    }
  }

  for (const dependentDimension of dependentDimensions) {
    const config = {
      backendFieldName: dependentDimension.name,
      displayName: dependentDimension.label,
      displayNameCol: `${dependentDimension.name}_label`,
      hidden: false,
      columnWidth: columnWidthForEachDependentDimension,
      colDefaultValue: getColDefaultValue(checkForMultiSelectability, dependentDimension),
      parentFieldsInGroup: [...dimensionsSeenSoFar],
      enableOnlyIfSet: [...dimensionsSeenSoFar],
      dependentFields: allDimensionNames.filter(
        (x) => ![...dimensionsSeenSoFar, dependentDimension.name].includes(x)
      ),
    };
    columnConfig.push(
      addAnySpecialPropsIfExistsAndReturnNewConfig(metricName, dependentDimension.name, config)
    );
    dimensionsSeenSoFar.push(dependentDimension.name);
  }

  if (shouldShowUsageContainer) {
    columnConfig.push({
      backendFieldName: METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_USAGE_BACKEND_NAME,
      displayName: getUsageLabelWithUnitForMetric(metricName),
      hidden: false,
      columnWidth: 2,
      enableOnlyIfSet: [...dimensionsSeenSoFar],
      headerTooltip: getUsageTooltipForMetric(metricName),
    });
  }

  columnConfig.push({
    backendFieldName: METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME,
    displayName: 'Discount',
    hidden: false,
    columnWidth: 2,
    colDefaultValue: null,
    type: TEXT_NUMBER_INPUT_TYPE,
    icon: 'percent',
    enableOnlyIfSet: [...dimensionsSeenSoFar],
  });

  columnConfig.push({
    backendFieldName: METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME,
    displayName: 'Metric Name',
    hidden: true,
    colDefaultValue: metricName,
  });

  return columnConfig;
};

export const getDimensionsColumnConfig = (columnsConfiguration, excludeHidden = true) => {
  let filteredList = columnsConfiguration.filter(
    (x) =>
      x.backendFieldName !== METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME &&
      x.backendFieldName !== METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_USAGE_BACKEND_NAME
  );

  if (excludeHidden) {
    filteredList = filteredList.filter((x) => x.hidden !== true);
  }

  return filteredList;
};

export const getDiscountColumnConfig = (columnsConfiguration) =>
  columnsConfiguration.filter(
    (x) => x.backendFieldName === METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME
  )[0];

const doDimensionValuesConflictHelper = (dimValues1, dimValues2, index) => {
  const CONFLICT_EXISTS = true;
  const NO_CONFLICT_EXISTS = false;

  if (index >= dimValues1.length) {
    return CONFLICT_EXISTS;
  }

  const val1 = dimValues1[index];
  const val2 = dimValues2[index];

  // Normalize both values to arrays
  const arr1 = Array.isArray(val1) ? val1 : [val1];
  const arr2 = Array.isArray(val2) ? val2 : [val2];

  /*
          If we are able to disambiguate at this index, then, we will say that there is no conflict, else, we will move on to the next index
         ABC   —  A1A2  — A11A12
        ABCD —  D1D2  —  D11D12
        e.g., in the above case, we can say that they don't conflict at index 1
   */

  // Check if arrays conflict
  if (!doArraysConflict(arr1, arr2)) {
    return NO_CONFLICT_EXISTS;
  }

  return doDimensionValuesConflictHelper(dimValues1, dimValues2, index + 1);
};

// Single function to handle array conflicts
const doArraysConflict = (arr1, arr2) => {
  // If either array contains ALL, it's a conflict
  if (arr1.includes(ALL_IN_SMALL_CASE) || arr2.includes(ALL_IN_SMALL_CASE)) {
    return true;
  }

  // Check for any overlapping values (conflict means they overlap)
  return arr1.some((val) => arr2.includes(val));
};

export const doDimensionValuesConflict = (dimValues1, dimValues2) => {
  if (!dimValues1 || !dimValues2 || dimValues1.length !== dimValues2.length) {
    throw 'Both the dimension values should be arrays and they need to be of the same length!';
  }
  return doDimensionValuesConflictHelper(dimValues1, dimValues2, 0);
};

const getIfElemRepresentsAVBDRow = (elem) => {
  const metricDiscountId = elem?.metric_discount_id;
  return (
    metricDiscountId &&
    (metricDiscountId.startsWith('CVBD-') || metricDiscountId.startsWith('VBD-'))
  );
};

export const formatMLDIntoFrontEndFormat = (metrics) => {
  const metricsToDimsObj = {};

  for (const elem of metrics) {
    const currMetricName = elem.metric_name;
    const currMetricId = elem.metric_id;
    const currMetricLabel = elem.metric_label;
    const keyToUse = `${currMetricName}$$$$${currMetricId}$$$$${currMetricLabel}`;

    if (!metricsToDimsObj[keyToUse]) {
      metricsToDimsObj[keyToUse] = [];
    }

    const currDimValuesObj = {
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME]: currMetricName,
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_MLD_ID_BACKEND_NAME]:
        elem[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_MLD_ID_BACKEND_NAME],
      [ROW_ID]: elem[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_MLD_ID_BACKEND_NAME],
      [IS_ROW_FROZEN]: true,
      [IS_ROW_OPENED_FOR_EDITING]: false,
      [IS_VBD]: getIfElemRepresentsAVBDRow(elem),
      [IS_ADMIN_EDITED_NAME]: elem?.[MLD_IS_ADMIN_EDITED_BACKEND_NAME],
      [IS_IMPORTED_BACKEND_NAME]: elem?.[IS_IMPORTED_BACKEND_NAME],
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_VALUES_BACKEND_NAME]:
        elem.dimensions.map((x) => x.dimension_name),
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_LABELS_BACKEND_NAME]:
        elem.dimensions.map((x) => x.dimension_label),
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_BACKEND_NAMES]: elem.dimensions.map(
        (x) => x.dimension
      ),
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME]: Number(
        elem.discount_value
      ),
    };

    for (const dim of elem.dimensions) {
      currDimValuesObj[dim.dimension] = dim.dimension_name;
    }
    metricsToDimsObj[keyToUse].push(currDimValuesObj);
  }

  const toRet = [];
  for (const key of Object.keys(metricsToDimsObj)) {
    toRet.push({
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME]: key.split('$$$$')[0],
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_ID_BACKEND_NAME]: key.split('$$$$')[1],
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_LABEL_BACKEND_NAME]: key.split('$$$$')[2],
      [IS_ROW_FROZEN]: true,
      [IS_ROW_OPENED_FOR_EDITING]: false,
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME]: metricsToDimsObj[key],
    });
  }

  return toRet;
};

export const getIfAllMetricsAreFrozen = (values) => {
  const mldConfig = values?.[CUSTOM_DISCOUNTS_JSON_BACKEND_NAME] ?? {};
  const metricRows = mldConfig?.metrics_level_discounts_array ?? [];

  if (metricRows.length === 0) {
    return true;
  }

  for (const metricRow of metricRows) {
    const dimensionRows = metricRow?.dimensions_config_array ?? [];

    if (dimensionRows.length === 0) {
      return true;
    }

    if (!dimensionRows.every((dimensionRow) => dimensionRow?.isRowFrozen === true)) {
      return false;
    }
  }

  return true;
};

export const getIfAnyMetricIsBeingEdited = (values) => {
  const mldConfig = values?.[CUSTOM_DISCOUNTS_JSON_BACKEND_NAME] ?? {};
  const metricRows = mldConfig?.metrics_level_discounts_array ?? [];

  for (const metricRow of metricRows) {
    const dimensionRows = metricRow?.dimensions_config_array ?? [];
    if (dimensionRows.some((dimensionRow) => dimensionRow?.isRowOpenedForEditing === true)) {
      return true;
    }
  }

  return false;
};

export const getIfRowIsVBD = (rowValues) => {
  return rowValues?.[IS_VBD] === true;
};

export const getMetricLevelDiscountRowsFromFormikValues = (formikValues) => {
  const customDiscounts = formikValues?.[CUSTOM_DISCOUNTS_JSON_BACKEND_NAME];
  return customDiscounts?.[METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME] || [];
};

export const getMetricNameFromMetricRow = (metricRow) => {
  return metricRow?.[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME];
};

export const getMetricNameFromRowValues = (rowValues) => {
  return rowValues?.[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME];
};

export const getMetricLabelFromMetricRow = (metricRow) => {
  return metricRow?.[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_LABEL_BACKEND_NAME];
};

export const getDimensionRowsFromMetricRow = (metricRow) => {
  return metricRow?.[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME] || [];
};

export const getGlobalDiscountFromFormikValues = (formikValues) => {
  return getIn(formikValues, CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME);
};

export const findVBDItemsLowerThanGlobalDiscount = (formikValues, newGlobalDiscount) => {
  const affectedMetrics = [];
  const metricsLevelDiscounts = getMetricLevelDiscountRowsFromFormikValues(formikValues);

  for (const metricRow of metricsLevelDiscounts) {
    const metricLabel = getMetricLabelFromMetricRow(metricRow);
    const dimensionRows = getDimensionRowsFromMetricRow(metricRow);
    const affectedDimensionRows = dimensionRows.filter((dimRow) => {
      //   For non-VBD rows, we should not perform this check
      if (!getIfRowIsVBD(dimRow)) {
        return false;
      }
      const discountValue =
        dimRow?.[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME];

      //  If the discount is null or undefined by any chance, we should not perform this check
      if (discountValue == null) {
        return false;
      }

      return newGlobalDiscount >= Number(discountValue);
    });

    if (affectedDimensionRows.length > 0) {
      affectedMetrics.push({
        metricLabel,
        dimensionRows: affectedDimensionRows,
      });
    }
  }

  return affectedMetrics;
};

// Get GD discount ranges from estimate's commit_config
export const getGDRangesFromEstimate = (estimate) => {
  return estimate?.commit_config?.[GD_RANGES_BACKEND_NAME] ?? [];
};

// Calculate Average ACV at List Price (Total Commit / Number of Years)
export const getAvgACVAtListPrice = (estimate) => {
  if (!estimate || !Array.isArray(estimate?.commit_summary?.before_discount)) {
    return 0;
  }

  const beforeDiscountArray = estimate?.commit_summary?.before_discount ?? [];
  const totalCommitKey = beforeDiscountArray.find((key) => key.field === 'Total Commit');
  const totalCommit = Number(totalCommitKey?.value ?? 0);
  const years = Number(estimate?.deal_duration?.deal_duration_years ?? 1);
  return years > 0 ? totalCommit / years : 0;
};

// Find the tier for a given ACV from the ranges
// NOTE: Ranges are expected to be non-overlapping with exclusive gaps (e.g., tier2.min = tier1.max + 1)
export const getGDTierForAverageACV = (avgACV, ranges) =>
  ranges?.find((tier) => avgACV >= tier.min_acv && avgACV <= tier.max_acv);

// Get the maximum discount allowed for non-admin users
// Max_GD_for_non_admin (including multi-year bonus) = Tier_max_discount + (years - 1)
// Ex: If Tier_max_discount = 5% and dealYears = 3, then Max_GD_for_non_admin = 5% + (3 - 1) = 7%
// NOTE: Multi-year bonus only applies when tierMax > 0
// For 0% tier (ACV < 25K), max remains 0% regardless of deal years
export const getMaxGDForNonAdmin = (avgACV, ranges, dealYears) => {
  const tier = getGDTierForAverageACV(avgACV, ranges);
  const tierMax = tier?.max_discount ?? 0;
  const multiYearBonus = Math.max(0, dealYears - 1);
  return tierMax === 0 ? 0 : tierMax + multiYearBonus;
};

export const getCurrentMLDRowDimensionValues = (rowValues) => {
  // For existing rows sent by BE, this is already created by formatMLDIntoFrontEndFormat function and is stored in the below key
  let currentRowDimValuesArray =
    rowValues[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_VALUES_BACKEND_NAME];

  /*
      For newly added rows, we need to fetch the values ourselves
     For this, we read dimensionBackEndNames, which contains the dimension names in the order we want.
     Post that, we just pick those values

     Note, currentRowDimValuesArray will be undefined in 2 cases
     Case 1: When the user is currently adding a new row
     Case 2: When the user has added and saved that row, then, we don't get these values immediately from BE
   */

  if (!currentRowDimValuesArray) {
    currentRowDimValuesArray = getValuesOfGivenKeysFromObjectAsArray(
      rowValues,
      rowValues[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_BACKEND_NAMES]
    );
  }

  return currentRowDimValuesArray;
};

export const getParentValuesOfGivenDimensionColumnForGivenMLDRow = (columnConfig, rowValues) => {
  const parentFieldsInGroup = columnConfig?.parentFieldsInGroup ?? [];
  return parentFieldsInGroup.map((x) => rowValues[x]);
};

export const getIfAllTheParentValuesOfGivenDimensionColumnForGivenMLDRowAreFilled = (
  columnConfig,
  rowValues
) => {
  const parentValues = getParentValuesOfGivenDimensionColumnForGivenMLDRow(columnConfig, rowValues);
  return areAllValuesInGivenArrayNonNullOrNonEmptyArray(parentValues);
};

export const getIfGivenMLDRowIsOpenedForEditing = (rowValues) => {
  return rowValues?.isRowOpenedForEditing === true;
};

export const getIfAtLeastOneMLDOrSGDiscountsExist = (formikValues) => {
  const metricsLevelDiscounts = getMetricLevelDiscountRowsFromFormikValues(formikValues);
  return metricsLevelDiscounts.length > 0;
};
